import { APITypes } from '../../primary-record-types';
import { getIntl } from '../../utils/intl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getProfileVisitInviteEmailsCacheKey, onUpdateProfileVisitInviteEmails } from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useNotify from '../useNotify';

const intl = getIntl('useProfileVisitInviteEmails', {
    errorLoadingEmails: 'Error loading emails',
    errorUpdatingEmails: 'Error updating emails',
    emailsUpdated: 'Subscriptions updated',
});

export type UseProfileVisitInviteEmailsArgs = {
    medicalProfileId: string;
    onUpdateEmails?: () => void;
};

const emptyList: any[] = [];

export type UseProfileVisitInviteEmailsReturnValue = {
    isLoading: boolean;
    emails: APITypes.medicalDataService.MedicalProfileVisitInviteEmailItem[];
    isLoadingEmails: boolean;
    updateEmails: (update: APITypes.medicalDataService.UpdateMedicalProfileVisitInviteEmailsRequest) => void;
    isUpdatingEmails: boolean;
};

export default function useProfileVisitInviteEmails({
    medicalProfileId,
    onUpdateEmails,
}: UseProfileVisitInviteEmailsArgs): UseProfileVisitInviteEmailsReturnValue {
    const notify = useNotify();
    const emailsQuery = useQuery<APITypes.medicalDataService.ListMedicalProfileVisitInviteEmailsResponse>({
        queryKey: getProfileVisitInviteEmailsCacheKey(medicalProfileId),
        queryFn: async () => {
            return await appAPI.get(urls.api.medicalData.visitInviteEmails(medicalProfileId));
        },
    });
    useQueryErrorNotify(emailsQuery, intl('emailsUpdated'));

    const updateEmailsMutation = useMutation({
        mutationFn: async (args: APITypes.medicalDataService.UpdateMedicalProfileVisitInviteEmailsRequest) => {
            return await appAPI.put(urls.api.medicalData.visitInviteEmails(medicalProfileId), {
                body: args,
            });
        },
        onError: () => {
            notify('error', intl('errorUpdatingEmails'));
        },
        onSuccess: () => {
            notify('success', intl('emailsUpdated'));
            onUpdateEmails?.();
            onUpdateProfileVisitInviteEmails.resetCache({ medicalProfileId })();
        },
    });

    return {
        isLoading: emailsQuery.isLoading,
        emails: emailsQuery.data?.medicalProfileVisitInviteEmails || emptyList,
        isLoadingEmails: emailsQuery.isLoading,
        updateEmails: updateEmailsMutation.mutate,
        isUpdatingEmails: updateEmailsMutation.isPending,
    };
}
